import React from 'react';
import cx from 'clsx';
import { useRouter } from 'next/router';
import { Button } from '@kcd/design-system';
import { trackEvent } from '@kcd/event-tracker';

import Descriptions from '~/components/Descriptions';
import { ADDRESS_ROUTER, VENDORS_ROUTER } from '~/constants/router';
import { useCurrentUser } from '~/domains/auth/queries';
import { formatDeliveryFee } from '~/domains/delivery/utils';
import { useLinkComponent } from '~/hooks/mobileApp';
import type { OAISchema } from '~/types/openapi';
import { openPageModal } from '~/utils/mobileApp';

import ProductOrderInfo from './ProductOrderInfo';

import { VendorIcon } from '~/assets/icons';
import MoveForward from '~/assets/icons/ic__system__chevron_right__outline.svg';
import ChevronRightIcon from '~/assets/icons/ic_chevron_right.svg';

function ProductDetailInfo({
  deliveryFee,
  deliveryFeeType,
  conditionalFreeAmount,
  deliveryPolicies,
  isDeliverableRegion,
  hasDefaultAddressRegion,
  canBeBundled,
  options,
  vendor,
  productName,
  offerId,
  className,
}: OAISchema['MarketProductDetailsResponseV2'] & { className?: string }) {
  const LinkComponent = useLinkComponent();
  const { query } = useRouter();
  const { isLoggedIn } = useCurrentUser();

  return (
    <section
      className={cx(['px-20 py-16 [&>h3:not(:first-child)]:mt-20', className])}
    >
      {hasDefaultAddressRegion ? (
        <Descriptions
          title={
            isDeliverableRegion && deliveryPolicies.length > 1
              ? '배송정보 (현재 설정하신 배송지 기준)'
              : '배송정보'
          }
          labelMinWidth={50}
        >
          <Descriptions.Item label="배송비">
            {isDeliverableRegion
              ? `${formatDeliveryFee({
                  conditionalFreeAmount,
                  deliveryFee: deliveryFee!,
                  deliveryFeeType: deliveryFeeType!,
                })!} ${canBeBundled ? '' : ' (개당)'}`
              : '지역별 상이'}
          </Descriptions.Item>
        </Descriptions>
      ) : (
        <>
          {isLoggedIn && (
            <Descriptions title="배송정보">
              <Button
                className="w-full"
                variant="outline"
                size="sm"
                onClick={() => openPageModal(ADDRESS_ROUTER.new())}
              >
                <span className="flex w-full items-center">
                  배송지 등록 하러가기
                  <MoveForward className="ml-4 text-[#0c1120]" />
                </span>
              </Button>
              <p className="mt-6 text-gray-1200 typo-paragraph100-regular">
                배송지에 따라 구매가능한 상품을 확인할 수 있어요.
              </p>
            </Descriptions>
          )}
        </>
      )}
      <ProductOrderInfo options={options} />

      {vendor && (
        <span
          role="presentation"
          onClick={() => {
            trackEvent('clicked button in 판매자 상품보기', {
              vendor,
              offerId,
              productName,
              vendorName: vendor.vendorName,
              searchText: decodeURIComponent(query.searchText as string),
            });
          }}
        >
          <LinkComponent href={VENDORS_ROUTER.vendorDetail(vendor.id!)}>
            <div className="mt-16 flex items-center justify-between border-t border-solid border-t-gray-200 pt-16">
              <p className="flex items-center typo-paragraph100-semibold">
                <VendorIcon className="mr-12 text-blue-600" />
                {vendor?.mallName}
              </p>
              <span
                role="presentation"
                className="flex items-center text-blue-600 typo-caption100-regular"
              >
                판매자 상품보기 <ChevronRightIcon />
              </span>
            </div>
          </LinkComponent>
        </span>
      )}
    </section>
  );
}

export default ProductDetailInfo;
